import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Obtém as iniciais do nome e sobrenome de uma pessoa
 * @param name Nome completo da pessoa
 * @returns String com as iniciais (ex: "João Silva" -> "JS")
 */
const LETTER = /\p{L}/u;

function wordsForInitials(name: string): string[] {
  const stripped = name
    .trim()
    .replace(/\s*\([^)]*\)/g, " ")
    .replace(/\s*\[[^\]]*]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return stripped.split(/\s+/).filter((w) => w.length > 0 && LETTER.test(w[0]!));
}

export function getUserInitials(name: string | null | undefined): string {
  if (!name || name.trim().length === 0) {
    return "U"; // Default para "User"
  }

  const parts = wordsForInitials(name);

  if (parts.length === 0) {
    const alnum = name.trim().replace(/[^\p{L}\p{N}]/gu, "");
    return (alnum[0] ?? "U").toUpperCase();
  }

  if (parts.length === 1) {
    return parts[0][0]?.toUpperCase() || "U";
  }

  const firstName = parts[0][0]?.toUpperCase() || "";
  const lastName = parts[parts.length - 1][0]?.toUpperCase() || "";

  return (firstName + lastName).slice(0, 2);
}

/**
 * Formata uma data para tempo relativo (ex: "há 2 horas", "ontem", "há 3 dias")
 * @param dateString Data em formato ISO string
 * @returns String com tempo relativo
 */
export function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) {
    return 'há alguns segundos';
  }

  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) {
    return `há ${diffInMinutes} ${diffInMinutes === 1 ? 'minuto' : 'minutos'}`;
  }

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    // Verificar se é hoje
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dateToday = new Date(date);
    dateToday.setHours(0, 0, 0, 0);
    
    if (dateToday.getTime() === today.getTime()) {
      return `há ${diffInHours} ${diffInHours === 1 ? 'hora' : 'horas'}`;
    }
    
    return `há ${diffInHours} ${diffInHours === 1 ? 'hora' : 'horas'}`;
  }

  const diffInDays = Math.floor(diffInHours / 24);
  
  // Verificar se é ontem
  if (diffInDays === 1) {
    return 'ontem';
  }
  
  if (diffInDays < 30) {
    return `há ${diffInDays} ${diffInDays === 1 ? 'dia' : 'dias'}`;
  }

  const diffInMonths = Math.floor(diffInDays / 30);
  if (diffInMonths < 12) {
    return `há ${diffInMonths} ${diffInMonths === 1 ? 'mês' : 'meses'}`;
  }

  const diffInYears = Math.floor(diffInMonths / 12);
  return `há ${diffInYears} ${diffInYears === 1 ? 'ano' : 'anos'}`;
}
